import { mdiChevronDown, mdiQrcodeScan } from '@mdi/js';
import Icon from '@mdi/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState, MouseEvent, useCallback, useContext, useMemo } from 'react';

import SystemNotice from './SystemNotice';
import Avatar from '@/components/Avatar';
import Logo from '@/components/Logo';

import { TOURNAMENT_CATEGORY, URL_ALLOW } from '@/constants';
import AuthContext from '@/contexts/auth';
import SchoolContext from '@/contexts/school';

import SettingsContext from '@/contexts/settings';
import TournamentContext from '@/contexts/tournament';
import useCurrentUser from '@/hooks/useCurrentUser';
import useDocumentClick from '@/hooks/useDocumentClick';
import { useLogout } from '@/hooks/useLogout';
import * as styles from '@/styles/components/Header';

const Header = () => {
  const { slug } = useContext(SchoolContext);

  const { schoolName } = useContext(SettingsContext);

  const [tooltipIsHidden, setTooltipState] = useState(true);
  const router = useRouter();
  const { logout } = useLogout();

  const { currentUser } = useContext(AuthContext);
  const { checkAccess } = useCurrentUser(currentUser?.uid);

  const { currentTournament, currentTournamentCategory } = useContext(TournamentContext);

  // ログインとパスワードリセットページではヘッダー非表示
  const hidden = useMemo(() => {
    return URL_ALLOW.includes(router.pathname);
  }, [router.pathname]);

  const pages = useMemo(() => {
    // Home Page
    if (
      (router.pathname === '/[schoolSlug]' && !router.pathname.includes('register')) ||
      router.pathname.includes('/[schoolSlug]/account')
    ) {
      return [
        {
          label: 'ホーム',
          path: `/${slug}`,
          dir: `/[schoolSlug]`,
          pathName: '/[schoolSlug]',
        },
        {
          label: 'アカウント',
          path: `/${slug}/account`,
          dir: `/[schoolSlug]/account`,
          pathName: '/[schoolSlug]/account',
        },
      ];
    }
    // Tournament list page
    else if (router.pathname == '/[schoolSlug]/tournament-list') {
      return [
        {
          label: 'ホーム',
          path: `/${slug}`,
          dir: `/[schoolSlug]`,
          pathName: '/[schoolSlug]',
          type: 'home',
        },
        {
          label: '大会一覧',
          path: `/${slug}/tournament-list`,
          dir: `/[schoolSlug]/tournament-list`,
          pathName: `/[schoolSlug]/tournament-list`,
        },
      ];
    }
    // Tournament pages
    else if (currentTournament && router.pathname.includes('tournament/[tournament_id]')) {
      // Normal tournament
      if (currentTournamentCategory === TOURNAMENT_CATEGORY.NORMAL) {
        return [
          {
            label: '大会一覧',
            path: `/${slug}/tournament-list`,
            dir: `/[schoolSlug]/tournament-list`,
            pathName: '/[schoolSlug]/tournament-list',
            type: 'home',
          },
          {
            label: '対戦表',
            path: `/${slug}/tournament/${router.query.tournament_id}`,
            dir: `/[schoolSlug]/tournament/[tournament_id]`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]`,
          },
          {
            label: `スケジュール`,
            path: `/${slug}/tournament/${router.query.tournament_id}/schedule`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
          },
          {
            label: `申込・選手一覧`,
            path: `/${slug}/tournament/${router.query.tournament_id}/apply-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/apply-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/apply-list`,
          },
          {
            label: `出欠管理`,
            path: `/${slug}/tournament/${router.query.tournament_id}/attendance-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/attendance-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/attendance-list`,
          },
          {
            label: `請求`,
            path: `/${slug}/tournament/${router.query.tournament_id}/claim`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/claim`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/claim`,
          },
          {
            label: '設定',
            path: `/${slug}/tournament/${router.query.tournament_id}/setting/basic-config`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/setting`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/setting/basic-config`,
          },
        ];
      }

      // Multiple tournament
      else if (currentTournamentCategory === TOURNAMENT_CATEGORY.MULTIPLE_TOURNAMENT) {
        return [
          {
            label: '大会一覧',
            path: `/${slug}/tournament-list`,
            dir: `/[schoolSlug]/tournament-list`,
            pathName: '/[schoolSlug]/tournament-list',
            type: 'home',
          },
          {
            label: '複数大会一覧',
            path: `/${slug}/tournament/${router.query.tournament_id}/sub-tournament-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/sub-tournament-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/sub-tournament-list`,
          },
          {
            label: 'スケジュール',
            path: `/${slug}/tournament/${router.query.tournament_id}/schedule`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
          },
          {
            label: '設定',
            path: `/${slug}/tournament/${router.query.tournament_id}/setting/multiple-basic-config`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/setting`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/setting/multiple-basic-config`,
          },
        ];
      }

      // Sub tournament
      else if (currentTournamentCategory === TOURNAMENT_CATEGORY.SUB_TOURNAMENT) {
        return [
          {
            label: '複数大会一覧',
            path: `/${slug}/tournament/${currentTournament.amateur_tournament_id}/sub-tournament-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/sub-tournament-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/sub-tournament-list`,
            type: 'home',
          },
          {
            label: '対戦表',
            path: `/${slug}/tournament/${router.query.tournament_id}`,
            dir: `/[schoolSlug]/tournament/[tournament_id]`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]`,
          },
          {
            label: 'スケジュール',
            path: `/${slug}/tournament/${currentTournament.amateur_tournament_id}/schedule?school_tournament_id=${currentTournament.id}`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/schedule`,
          },
          {
            label: `申込・選手一覧`,
            path: `/${slug}/tournament/${router.query.tournament_id}/apply-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/apply-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/apply-list`,
          },
          {
            label: `出欠管理`,
            path: `/${slug}/tournament/${router.query.tournament_id}/attendance-list`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/attendance-list`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/attendance-list`,
          },
          {
            label: `請求`,
            path: `/${slug}/tournament/${router.query.tournament_id}/claim`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/claim`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/claim`,
          },
          {
            label: '設定',
            path: `/${slug}/tournament/${router.query.tournament_id}/setting/basic-config`,
            dir: `/[schoolSlug]/tournament/[tournament_id]/setting`,
            pathName: `/[schoolSlug]/tournament/[tournament_id]/setting/basic-config`,
          },
        ];
      }

      return [];
    }

    // facility pages
    else if (router.pathname.includes('/[schoolSlug]/rental/[facility_id]')) {
      return [
        {
          label: '施設一覧',
          path: `/${slug}/rental/facility-list`,
          dir: `/[schoolSlug]/rental/facility-list`,
          pathName: '/[schoolSlug]/rental/facility-list',
          type: 'home',
        },
        {
          label: 'レンタル施設一覧',
          path: `/${slug}/rental/${router.query.facility_id}/court-list`,
          dir: `/[schoolSlug]/rental/[facility_id]/court-list`,
          pathName: `/[schoolSlug]/rental/[facility_id]/court-list`,
        },
        {
          label: 'スケジュール',
          path: `/${slug}/rental/${router.query.facility_id}/schedule`,
          dir: `/[schoolSlug]/rental/[facility_id]/schedule`,
          pathName: `/[schoolSlug]/rental/[facility_id]/schedule`,
        },
        {
          label: '申込者一覧',
          path: `/${slug}/rental/${router.query.facility_id}/apply-list`,
          dir: `/[schoolSlug]/rental/[facility_id]/apply-list`,
          pathName: `/[schoolSlug]/rental/[facility_id]/apply-list`,
        },
        {
          label: '設定',
          path: `/${slug}/rental/${router.query.facility_id}/setting/basic-setting`,
          dir: `/[schoolSlug]/rental/[facility_id]/setting`,
          pathName: `/[schoolSlug]/rental/[facility_id]/setting/basic-setting`,
        },
      ];
    }
    // rental pages
    else if (router.pathname.includes('/[schoolSlug]/rental')) {
      return [
        {
          label: 'ホーム',
          path: `/${slug}`,
          dir: `/[schoolSlug]`,
          pathName: '/[schoolSlug]',
          type: 'home',
        },
        {
          label: '施設一覧',
          path: `/${slug}/rental/facility-list`,
          dir: `/[schoolSlug]/rental/facility-list`,
          pathName: '/[schoolSlug]/rental/facility-list',
        },
        {
          label: '設定',
          path: `/${slug}/rental/setting`,
          dir: `/[schoolSlug]/rental/setting`,
          pathName: '/[schoolSlug]/rental/setting',
        },
      ];
    }

    // School pages
    else {
      return [
        {
          label: 'ホーム',
          path: `/${slug}`,
          dir: `/[schoolSlug]`,
          pathName: '/[schoolSlug]',
          type: 'home',
        },
        {
          label: 'ダッシュボード',
          path: `/${slug}/dashboard`,
          dir: `/[schoolSlug]/dashboard`,
          pathName: '/[schoolSlug]/dashboard',
        },
        {
          label: 'スケジュール',
          path: `/${slug}/schedule`,
          dir: `/[schoolSlug]/schedule`,
          pathName: '/[schoolSlug]/schedule',
        },
        {
          label: 'レッスン',
          path: `/${slug}/lesson`,
          dir: `/[schoolSlug]/lesson`,
          pathName: '/[schoolSlug]/lesson',
        },
        {
          label: 'イベント ',
          path: `/${slug}/event`,
          dir: `/[schoolSlug]/event`,
          pathName: '/[schoolSlug]/event',
        },
        {
          label: '体験予約',
          path: `/${slug}/reservation`,
          dir: `/[schoolSlug]/reservation`,
          pathName: `/[schoolSlug]/reservation`,
        },
        {
          label: '会員',
          path: `/${slug}/member-management/member`,
          dir: `/[schoolSlug]/member`,
          pathName: `/[schoolSlug]/member-management/member`,
        },
        {
          label: '請求',
          path: `/${slug}/payment/billing`,
          dir: `/[schoolSlug]/payment`,
          pathName: `/[schoolSlug]/payment/billing`,
        },
        {
          label: '設定',
          path: `/${slug}/setting/school`,
          dir: `/[schoolSlug]/setting`,
          pathName: `/[schoolSlug]/setting/school`,
        },
      ];
    }
  }, [router, slug, currentTournament, currentTournamentCategory]);

  const faqUrl = useMemo(() => {
    if (router.pathname.includes('tournament')) {
      return 'https://tayori.com/q/spohabi-tournament';
    }
    if (router.pathname.includes('rental')) {
      return '';
    }
    return 'https://tayori.com/q/spohabi-school';
  }, [router.pathname]);

  const handleTriggerClick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setTooltipState(!tooltipIsHidden);
    },
    [tooltipIsHidden],
  );

  const handleDocumentClick = useCallback(() => {
    if (!tooltipIsHidden) {
      setTooltipState(true);
    }
  }, [tooltipIsHidden]);

  useDocumentClick(handleDocumentClick);

  const matchSlug = (dir: string): boolean => {
    if (dir === `/[schoolSlug]`) {
      return router.pathname === `/[schoolSlug]`;
    } else {
      if (dir === `/[schoolSlug]/tournament/[tournament_id]`) {
        return router.pathname === `/[schoolSlug]/tournament/[tournament_id]`;
      } else if (router.pathname.includes(dir)) {
        return true;
      } else {
        return router.pathname.includes(`/account`) && dir === `/${slug}/setting`;
      }
    }
  };

  /**
   * Handle redirect to school
   */
  const redirectToSchool = () => {
    router.push(`/${slug}/`);
  };

  if (hidden) {
    return <div css={styles.groupHeader}>{<SystemNotice />}</div>;
  } else {
    return (
      <div css={styles.groupHeader}>
        {<SystemNotice />}
        <header css={styles.root}>
          {/* 左 */}
          <div css={styles.title} onClick={redirectToSchool}>
            <a>
              <Logo width={142} />
            </a>
            <div
              className={`${schoolName.length > 25 ? 'text-ellipsis' : ''} uk-flex uk-flex-center`}
              css={styles.schoolname}>
              <span>{schoolName}</span>
            </div>
          </div>

          {/* 中央 */}
          <nav css={styles.navigation}>
            {pages.map((page) => {
              return (
                checkAccess(page.pathName) && (
                  <div style={{ height: '100%' }} className='uk-flex uk-flex-middle'>
                    <Link href={page.path} passHref key={page.path}>
                      <a css={[styles.link, matchSlug(page.dir) ? styles.current : null]}>{page.label}</a>
                    </Link>
                    {page.type === 'home' && <div css={styles.divider}></div>}
                  </div>
                )
              );
            })}
          </nav>

          {/* 右 */}
          <div css={styles.tools}>
            {router.pathname !== '/[schoolSlug]' &&
              !router.pathname.includes('register') &&
              !router.pathname.includes('rental') &&
              !router.pathname.includes('/[schoolSlug]/account') && (
                <a css={styles.faq} aria-label='FAQ' target='_blank' href={faqUrl} rel='noreferrer'>
                  FAQ
                </a>
              )}

            {!(
              (router.pathname === '/[schoolSlug]' && !router.pathname.includes('register')) ||
              router.pathname.includes('/[schoolSlug]/tournament-list') ||
              router.pathname.includes('tournament/[tournament_id]') ||
              router.pathname.includes('/[schoolSlug]/account') ||
              router.pathname.includes('/[schoolSlug]/rental')
            ) && (
              <Link href={`/${slug}/reader-qr`} passHref>
                <a css={styles.open} aria-label='ユーザーページを開く'>
                  <Icon path={mdiQrcodeScan} title='' size='2.4rem' />
                </a>
              </Link>
            )}

            <div css={styles.account}>
              <button type='button' css={styles.trigger} onClick={handleTriggerClick} aria-label='ツールチップを開く'>
                <Avatar />
                <span css={styles.arrow}>
                  <Icon path={mdiChevronDown} title='' size='2.3rem' />
                </span>
              </button>
              <div css={styles.tooltip} aria-hidden={tooltipIsHidden}>
                <Link href={`/${slug}/setting/account/information`} passHref>
                  <a css={styles.accountSettingLink}>アカウント設定</a>
                </Link>

                <button
                  type='button'
                  css={styles.logoutBtn}
                  onClick={() => {
                    logout();
                    router.push(`/${slug}/login`);
                  }}
                  aria-label='ログアウトする'>
                  ログアウト
                </button>
              </div>
            </div>
          </div>
        </header>
      </div>
    );
  }
};

export default Header;
